import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

const PAGE_SIZE = 100;

/** Query key for admin households list – invalidate this when creating/updating households elsewhere */
export const ADMIN_HOUSEHOLDS_QUERY_KEY = ['admin', 'households'] as const;

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 1.8rem;
  color: var(--onSurface);
`;

const SearchInput = styled.input`
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  border: 1px solid var(--outline);
  font-size: 0.9rem;
  min-width: 200px;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(31, 117, 254, 0.2);
  }
`;

const TableCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  overflow: hidden;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--outline);
    text-align: left;
    font-size: 0.9rem;
    color: var(--onSurfaceVariant);
  }

  th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--onSurfaceVariant);
    background: var(--surfaceVariant);
  }

  tbody tr:hover {
    background: var(--surfaceVariant);
  }
  @media (max-width: 768px) {
    th,
    td {
      padding: 0.5rem 0.75rem;
      font-size: 0.85rem;
    }
  }
`;

const NameLink = styled(Link)`
  color: var(--primary);
  text-decoration: none;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

const BackLink = styled(Link)`
  color: var(--onSurfaceVariant);
  font-size: 0.9rem;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 1rem;

  &:hover {
    color: var(--primary);
  }
`;

const EmptyState = styled.p`
  margin: 0;
  padding: 2rem;
  text-align: center;
  color: var(--onSurfaceVariant);
`;

const LoadingState = styled.p`
  margin: 0;
  padding: 2rem;
  text-align: center;
  color: var(--onSurfaceVariant);
`;

const TableFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 1rem;
  color: var(--onSurfaceVariant);
  font-size: 0.8rem;
  border-top: 1px solid var(--outline);
  background: var(--surface);
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  min-width: 32px;
  height: 32px;
  padding: 0 0.55rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: ${({ $active }) => ($active ? 'var(--primary)' : 'var(--surface)')};
  color: ${({ $active }) => ($active ? 'var(--surface)' : 'var(--onSurfaceVariant)')};
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${({ $active }) => ($active ? '#1a63d6' : '#f1f5f9')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageEllipsis = styled.span`
  padding: 0 0.4rem;
  color: var(--onSurfaceVariant);
  font-weight: 600;
`;

interface HouseholdRow {
  id: string;
  name: string | null;
  address: string | null;
  phone: string | null;
  primary_contact_id: string | null;
}

interface UserRow {
  id: string;
  full_name: string | null;
}

export const HouseholdsPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const { data: households = [], isLoading, error } = useQuery({
    queryKey: ADMIN_HOUSEHOLDS_QUERY_KEY,
    queryFn: async () => {
      const PAGE = 1000; // Supabase/PostgREST default max is 1000; fetch in chunks to get all
      const all: HouseholdRow[] = [];
      let from = 0;
      while (true) {
        const to = from + PAGE - 1;
        const { data, error: fetchError } = await supabase
          .from('households')
          .select('id, name, address, phone, primary_contact_id')
          .order('name', { ascending: true })
          .range(from, to);
        if (fetchError) throw fetchError;
        const chunk = (data ?? []) as HouseholdRow[];
        all.push(...chunk);
        if (chunk.length < PAGE) break;
        from += PAGE;
      }
      return all;
    },
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const primaryContactIds = useMemo(
    () => Array.from(new Set(households.map((h) => h.primary_contact_id).filter(Boolean))) as string[],
    [households]
  );

  const { data: primaryContacts = [] } = useQuery({
    queryKey: [...ADMIN_HOUSEHOLDS_QUERY_KEY, 'primary-contacts', primaryContactIds],
    enabled: primaryContactIds.length > 0,
    queryFn: async () => {
      const { data, error: fetchError } = await supabase
        .from('users')
        .select('id, full_name')
        .in('id', primaryContactIds);
      if (fetchError) throw fetchError;
      return (data ?? []) as UserRow[];
    },
  });

  const contactLookup = useMemo(
    () => Object.fromEntries(primaryContacts.map((u) => [u.id, u.full_name ?? ''])),
    [primaryContacts]
  );

  const filteredHouseholds = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return households;
    return households.filter(
      (h) =>
        (h.name ?? '').toLowerCase().includes(term) ||
        (h.address ?? '').toLowerCase().includes(term) ||
        (h.phone ?? '').toLowerCase().includes(term) ||
        (contactLookup[h.primary_contact_id ?? ''] ?? '').toLowerCase().includes(term)
    );
  }, [households, search, contactLookup]);

  const totalCount = filteredHouseholds.length;
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCount / PAGE_SIZE)),
    [totalCount]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const pageIndicators = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }
    const indicators: Array<number | 'ellipsis'> = [1];
    const start = Math.max(2, currentPage - 2);
    const end = Math.min(totalPages - 1, currentPage + 2);
    if (start > 2) indicators.push('ellipsis');
    for (let page = start; page <= end; page += 1) indicators.push(page);
    if (end < totalPages - 1) indicators.push('ellipsis');
    indicators.push(totalPages);
    return indicators;
  }, [currentPage, totalPages]);

  const paginatedHouseholds = useMemo(() => {
    const from = (currentPage - 1) * PAGE_SIZE;
    return filteredHouseholds.slice(from, from + PAGE_SIZE);
  }, [filteredHouseholds, currentPage]);

  return (
    <Page>
      <BackLink to="/admin/users">← Back to Users & Roles</BackLink>
      <HeaderRow>
        <HeaderTitle>Households</HeaderTitle>
        <SearchInput
          type="search"
          placeholder="Search by name, address, phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search households"
        />
      </HeaderRow>
      <TableCard>
        {error ? (
          <EmptyState>Failed to load households. Please try again.</EmptyState>
        ) : isLoading ? (
          <LoadingState>Loading households…</LoadingState>
        ) : filteredHouseholds.length === 0 ? (
          <EmptyState>
            {households.length === 0 ? 'No households yet.' : 'No households match your search.'}
          </EmptyState>
        ) : (
          <>
          <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Address</th>
                <th>Phone</th>
                <th>Primary contact</th>
              </tr>
            </thead>
            <tbody>
              {paginatedHouseholds.map((h) => (
                <tr key={h.id}>
                  <td>{h.name ?? '—'}</td>
                  <td>{h.address ?? '—'}</td>
                  <td>{h.phone ?? '—'}</td>
                  <td>
                    {h.primary_contact_id ? (
                      <NameLink to={`/admin/members/${h.primary_contact_id}`}>
                        {contactLookup[h.primary_contact_id] || 'View member'}
                      </NameLink>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          </TableWrap>
          <TableFooter>
            <span>
              {totalCount === 0
                ? 'Showing 0 of 0'
                : `Showing ${(currentPage - 1) * PAGE_SIZE + 1}-${Math.min(
                    currentPage * PAGE_SIZE,
                    totalCount
                  )} of ${totalCount}`}
            </span>
            {totalPages > 1 && (
              <Pagination>
                <PageButton
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  {'<'}
                </PageButton>
                {pageIndicators.map((indicator, index) =>
                  indicator === 'ellipsis' ? (
                    <PageEllipsis key={`ellipsis-${index}`}>...</PageEllipsis>
                  ) : (
                    <PageButton
                      key={indicator}
                      type="button"
                      $active={indicator === currentPage}
                      onClick={() => setCurrentPage(indicator)}
                      aria-label={`Page ${indicator}`}
                    >
                      {indicator}
                    </PageButton>
                  )
                )}
                <PageButton
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  {'>'}
                </PageButton>
              </Pagination>
            )}
          </TableFooter>
          </>
        )}
      </TableCard>
    </Page>
  );
};

export default HouseholdsPage;
